import React, { useMemo, useState } from "react";

const getInitials = (value = "") => {
  const words = String(value).trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "U";
  return words
    .slice(0, 2)
    .map((word) => word.charAt(0).toUpperCase())
    .join("");
};

const getAccountId = (account) =>
  account?.userId || account?._id || account?.id || account?.user?._id || "";

const getAccountName = (account) => {
  if (account?.accountType === "professional") {
    return (
      account?.businessName ||
      [account?.firstName, account?.lastName].filter(Boolean).join(" ") ||
      account?.username ||
      "Business profile"
    );
  }

  return (
    [account?.firstName, account?.lastName].filter(Boolean).join(" ") ||
    account?.username ||
    "Homeowner profile"
  );
};

function SelectAccountScreen({
  accounts = [],
  phoneNumber = "",
  onSelectAccount,
  onCreateNewAccount,
  onBack,
  isLoading = false,
  eyebrowText = "Post your requirement",
  selectLabel = "Select a profile to post this requirement",
  continueLabel = "Post the requirement",
  loadingLabel = "Posting...",
}) {
  const [selectedAccountId, setSelectedAccountId] = useState("");
  const maskedPhone = useMemo(() => {
    const value = String(phoneNumber || "");
    if (value.length < 5) return value;
    return `${value.slice(0, 3)}${"*".repeat(Math.max(0, value.length - 5))}${value.slice(-2)}`;
  }, [phoneNumber]);

  const selectedAccount = useMemo(
    () => accounts.find((account) => getAccountId(account) === selectedAccountId),
    [accounts, selectedAccountId],
  );

  const homeownerAccounts = useMemo(
    () => accounts.filter((account) => account?.accountType === "personal"),
    [accounts],
  );
  const professionalAccounts = useMemo(
    () => accounts.filter((account) => account?.accountType === "professional"),
    [accounts],
  );

  const renderAccount = (account) => {
    const accountId = getAccountId(account);
    const name = getAccountName(account);
    const profileImage = account?.profile || account?.profileImage || account?.avatar;
    const selected = selectedAccountId === accountId;

    return (
      <button
        type="button"
        className={`req-account-card ${selected ? "is-selected" : ""}`}
        key={accountId || name}
        onClick={() => setSelectedAccountId(accountId)}
      >
        <span className="req-account-info">
          {profileImage ? (
            <img className="req-account-avatar" src={profileImage} alt="" />
          ) : (
            <span className="req-account-avatar req-account-avatar-fallback">
              {getInitials(name)}
            </span>
          )}
          <span className="req-account-details">
            <span className="req-account-name-row">
              <span>{name}</span>
              {account?.isPaid ? <span className="req-account-verified" /> : null}
            </span>
          </span>
        </span>
        <span className="req-account-radio" />
      </button>
    );
  };

  const renderGroup = (title, groupAccounts) =>
    groupAccounts.length > 0 ? (
      <section className="req-account-group">
        <h2>{title}</h2>
        {groupAccounts.map(renderAccount)}
      </section>
    ) : null;

  return (
    <div className="req-screen req-screen-accounts">
      <div className="req-content">
        <div className="req-inline-header">
          <button
            type="button"
            className="req-back-icon-btn"
            onClick={onBack}
            aria-label="Go back"
          >
            <img src="/caretLeft.svg" alt="" />
          </button>
          <p className="req-eyebrow">{eyebrowText}</p>
        </div>

        <div className="req-account-title-wrap">
          <h1 className="req-account-title">Welcome back</h1>
          <p className="req-account-subtitle">We found profiles linked to your number with this category</p>
          <p className="req-account-phone">+91 {maskedPhone}</p>
        </div>

        <p className="req-account-select-label">{selectLabel}</p>

        <div className="req-account-list">
          {renderGroup("Homeowner profile", homeownerAccounts)}
          {renderGroup("Business", professionalAccounts)}
        </div>
      </div>

      <div className="req-footer">
        <div className="req-account-or-row">
          <span />
          <p>or</p>
          <span />
        </div>
        <button
          type="button"
          className="req-account-create-btn"
          onClick={onCreateNewAccount}
          disabled={isLoading}
        >
          Create new account
        </button>
        <div className="req-footer-actions req-footer-actions-select">
          <button type="button" className="req-secondary-btn" onClick={onBack}>
            Go back
          </button>
          <button
            type="button"
            className="req-primary-btn"
            onClick={() => selectedAccount && onSelectAccount(selectedAccount)}
            disabled={!selectedAccount || isLoading}
          >
            {isLoading ? loadingLabel : continueLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export { getAccountId };
export default SelectAccountScreen;
